import { SearchIcon, XIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export interface FilterOption {
  readonly value: string;
  readonly label: string;
}

export interface FilterSpec {
  readonly id: string;
  readonly label: string;
  readonly value: string | undefined;
  readonly options: readonly FilterOption[];
  readonly onChange: (value: string | undefined) => void;
}

export interface TableToolbarProps {
  readonly searchValue: string;
  readonly searchPlaceholder: string;
  readonly onSearchChange: (value: string) => void;
  readonly filters: readonly FilterSpec[];
  readonly isFiltered: boolean;
  readonly onReset: () => void;
  readonly action?: ReactNode;
}

/** Matches the design. Long enough to stop a request per keystroke. */
const SEARCH_DEBOUNCE_MS = 260;

/**
 * Radix has no concept of an empty option — `value=""` clears the select
 * rather than selecting something — so "no filter" needs a value of its own.
 */
const ALL = '__all__';

export function TableToolbar({
  searchValue,
  searchPlaceholder,
  onSearchChange,
  filters,
  isFiltered,
  onReset,
  action,
}: TableToolbarProps) {
  const searchId = useId();
  const [draft, setDraft] = useState(searchValue);
  const [syncedValue, setSyncedValue] = useState(searchValue);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * Keeps the box in step when the value changes from outside — a reset, or
   * the back button restoring an earlier search from the URL.
   *
   * Adjusted during render rather than in an effect: React re-runs this
   * component immediately without committing the intermediate DOM, whereas an
   * effect would paint the stale value first and then correct it.
   */
  if (searchValue !== syncedValue) {
    setSyncedValue(searchValue);
    setDraft(searchValue);
  }

  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );

  return (
    <div className="flex flex-wrap items-center gap-[9px]">
      <label htmlFor={searchId} className="sr-only">
        {searchPlaceholder}
      </label>
      <div className="relative min-w-[240px] flex-1">
        <SearchIcon
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id={searchId}
          type="search"
          value={draft}
          placeholder={searchPlaceholder}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => {
              onSearchChange(next);
            }, SEARCH_DEBOUNCE_MS);
          }}
          className="h-[34px] pl-9 text-[13px]"
        />
      </div>

      {filters.map((filter) => (
        <FilterSelect key={filter.id} filter={filter} />
      ))}

      {isFiltered ? (
        <Button variant="ghost" onClick={onReset} className="shrink-0 text-muted-foreground">
          <XIcon aria-hidden="true" className="size-3.5" />
          Reset
        </Button>
      ) : null}

      {action === undefined ? null : <div className="ml-auto">{action}</div>}
    </div>
  );
}

/**
 * The trigger always names its filter — "Kind: all", "Kind: dish" — rather
 * than showing a bare value. On a toolbar with six of them, a row of
 * unlabelled dropdowns is a puzzle: you have to open each one to find out
 * what it filters.
 */
function FilterSelect({ filter }: { readonly filter: FilterSpec }) {
  const selected = filter.options.find((option) => option.value === filter.value);

  return (
    <Select
      value={filter.value ?? ALL}
      onValueChange={(next) => {
        filter.onChange(next === ALL ? undefined : next);
      }}
    >
      <SelectTrigger
        aria-label={filter.label}
        className={cn(
          'h-[34px] shrink-0 gap-1.5 text-[13px]',
          filter.value === undefined ? 'text-muted-foreground' : 'border-primary/40',
        )}
      >
        <SelectValue>
          <span className="text-muted-foreground">{filter.label}:</span>
          <span className={filter.value === undefined ? '' : 'font-medium text-foreground'}>
            {selected?.label ?? 'all'}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        <SelectSeparator />
        {filter.options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
